<template>
  <div id="stats">
    <v-carousel
      v-model="selectedChart"
      hide-delimiters
      height="400"
      v-show="!loading"
    >
      <v-carousel-item eager>
        <highcharts
          :constructorType="'stockChart'"
          class="hc"
          :options="betStatsOptions"
          ref="betStats"
        >
        </highcharts>
      </v-carousel-item>
      <v-carousel-item eager>
        <highcharts class="hc" :options="dayStatsOptions" ref="dayStats">
        </highcharts>
      </v-carousel-item>
    </v-carousel>
    <v-container fluid v-show="!loading" id="stats-filter">
      <v-card flat id="s-summary">
        <v-card-title>
          Summary
          <a :href="csvURL" download="slippy_bets.csv" class="b-csv">
            Download CSV
          </a>
          <v-icon @click="refresh(true)">
            mdi-refresh
          </v-icon>
        </v-card-title>
        <v-card-text>
          <div class="s-item">
            <span class="s-name">EV:</span>
            <span class="s-value">{{ formatCurrency(this.summary.ev) }}</span>
          </div>
          <div class="s-item">
            <span class="s-name">Profit:</span>
            <span class="s-value">
              {{ formatCurrency(this.summary.profit) }}
            </span>
          </div>
          <div class="s-item">
            <span class="s-name">Bets Placed:</span>
            <span class="s-value">{{ this.summary.bets }}</span>
          </div>
          <div class="s-item">
            <span class="s-name">Ex. Margin:</span>
            <span class="s-value">{{ this.summary.margin.toFixed(2) }}%</span>
          </div>
        </v-card-text>
      </v-card>
      <v-card flat id="s-bookies">
        <v-card-title>
          Bookies
          <div
            v-if="allBookiesSelected"
            class="b-select"
            @click="() => toggleBookies(false)"
          >
            Clear all
          </div>
          <div v-else class="b-select" @click="() => toggleBookies(true)">
            Select all
          </div>
        </v-card-title>
        <v-card-text>
          <v-container pa-0>
            <v-row dense>
              <v-col>
                <v-checkbox
                  v-for="[bookie] in Object.entries(filters.bookies).slice(
                    0,
                    Math.ceil(Object.keys(filters.bookies).length / 2)
                  )"
                  v-model="filters.bookies[bookie]"
                  :label="bookie.toString()"
                  :key="bookie"
                  hide-details
                  class="filter-bookie"
                ></v-checkbox>
              </v-col>
              <v-col>
                <v-checkbox
                  v-for="[bookie] in Object.entries(filters.bookies).slice(
                    Math.ceil(Object.keys(filters.bookies).length / 2)
                  )"
                  v-model="filters.bookies[bookie]"
                  :label="bookie"
                  :key="bookie"
                  hide-details
                  class="filter-bookie"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
      <v-card flat id="s-types">
        <v-card-title>
          Race Types
        </v-card-title>
        <v-card-text>
          <v-checkbox
            v-for="(enabled, type) in filters.race_types"
            v-model="filters.race_types[type]"
            :label="raceTypeDescription(type)"
            :key="type"
            hide-details
            class="filter-type"
          ></v-checkbox>
        </v-card-text>
      </v-card>
      <v-card flat id="s-markets">
        <v-card-title>
          Markets
        </v-card-title>
        <v-card-text>
          <v-checkbox
            v-for="(enabled, type) in filters.markets"
            v-model="filters.markets[type]"
            :label="marketDescription(type)"
            :key="type"
            hide-details
            class="filter-market"
          ></v-checkbox>
        </v-card-text>
      </v-card>
      <v-card flat id="s-states">
        <v-card-title>
          States
        </v-card-title>
        <v-card-text>
          <v-container pa-0 ma-0>
            <v-row no-gutters>
              <v-col>
                <v-checkbox
                  v-model="filters.states.ACT"
                  label="ACT"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.NSW"
                  label="NSW"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.NT"
                  label="NT"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.QLD"
                  label="QLD"
                  hide-details
                />
              </v-col>
              <v-col>
                <v-checkbox
                  v-model="filters.states.SA"
                  label="SA"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.TAS"
                  label="TAS"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.VIC"
                  label="VIC"
                  hide-details
                />
                <v-checkbox
                  v-model="filters.states.WA"
                  label="WA"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
      <v-card flat id="s-dates">
        <v-card-title>
          Dates
        </v-card-title>
        <v-card-text>
          <v-checkbox
            v-model="filters.dates.all"
            :label="`All Dates`"
          ></v-checkbox>
          <v-date-picker
            v-if="!filters.dates.all"
            v-model="filters.dates.range"
            range
          ></v-date-picker>
        </v-card-text>
      </v-card>
    </v-container>
    <v-container fill-height fluid class="load-parent">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        class="loader"
      ></v-progress-circular>
    </v-container>
  </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import moment from "moment";
import * as History from "../lib/bet_history";
import { currency } from "../lib/formatting";

export default {
  name: "Statistics",
  components: {},
  data() {
    return {
      loading: true,
      selectedChart: 0,
      csvURL: "",
      data: {
        bets: [],
        ev: []
      },
      boost: {
        enabled: true
      },
      summary: {
        profit: 0,
        ev: 0,
        bets: 0,
        margin: 0
      },
      filters: {
        dates: {
          all: true,
          range: []
        },
        bookies: {},
        bookie_clear: undefined,
        race_types: {
          T: true,
          H: true,
          G: true
        },
        markets: {
          W: true,
          P: true,
          T: true,
          F: true
        },
        states: {
          ACT: true,
          NSW: true,
          NT: true,
          QLD: true,
          SA: true,
          TAS: true,
          VIC: true,
          WA: true
        }
      },
      dayStatsOptions: {
        chart: {
          type: "column"
        },

        title: {
          text: "Day Summary"
        },

        xAxis: {
          type: "datetime"
        },

        yAxis: {
          title: "Value"
        },

        time: {
          useUTC: false
        },

        tooltip: {
          shared: true
        },

        plotOptions: {
          series: {
            states: {
              inactive: {
                opacity: 1
              }
            },
            events: {
              legendItemClick: function() {
                this.chart.series.forEach(function(s) {
                  if (s !== this && s.visible) {
                    s.hide();
                  }
                });

                return !this.visible ? true : false;
              }
            }
          }
        },

        series: [
          {
            name: "EV",
            id: "ev",
            data: [],
            visible: true,
            tooltip: {
              valueDecimals: 2,
              valuePrefix: "$ "
            }
          },
          {
            name: "Profit",
            id: "profit",
            data: [],
            visible: false,
            color: "#f078a4",
            tooltip: {
              valueDecimals: 2,
              valuePrefix: "$ "
            }
          },
          {
            name: "# Bets",
            id: "bets",
            color: "#41e038",
            data: [],
            visible: false
          },
          {
            type: "sma",
            name: "7 Day Avg",
            linkedTo: "profit",
            tooltip: {
              valueDecimals: 2,
              valuePrefix: "$ "
            },
            color: "#9c1f4d",
            marker: {
              enabled: false
            },
            params: {
              period: 7
            }
          },
          {
            type: "sma",
            name: "7 Day Avg",
            linkedTo: "ev",
            tooltip: {
              valueDecimals: 2,
              valuePrefix: "$ "
            },
            color: "#2a4191",
            marker: {
              enabled: false
            },
            params: {
              period: 7
            }
          },
          {
            type: "sma",
            name: "7 Day Avg",
            linkedTo: "bets",
            tooltip: {
              valueDecimals: 0
            },
            color: "#28943c",
            marker: {
              enabled: false
            },
            params: {
              period: 7
            }
          }
        ],

        exporting: {
          menuItemDefinitions: {
            avg7Day: {
              text: "7 Day Average",
              onclick: () => {
                const chart = this.$refs.dayStats.chart;
                if (!chart.series) return;

                let visible = chart.series.find(s => s.visible && !s.bindTo)
                  .options.id;

                chart.series.forEach(s => {
                  if (
                    s.options &&
                    s.options.linkedTo &&
                    s.options.linkedTo === visible &&
                    s.options.params &&
                    s.options.params.period === 28
                  ) {
                    s.options.name = "7 Day Avg";
                    s.options.params.period = 7;
                    s.update(s.options);
                  }
                });
              }
            },
            avg28Day: {
              text: "28 Day Average",
              onclick: () => {
                const chart = this.$refs.dayStats.chart;
                if (!chart.series) return;

                let visible = chart.series.find(s => s.visible && !s.bindTo)
                  .options.id;

                chart.series.forEach(s => {
                  if (
                    s.options &&
                    s.options.linkedTo &&
                    s.options.linkedTo === visible &&
                    s.options.params &&
                    s.options.params.period === 7
                  ) {
                    s.options.name = "28 Day Avg";
                    s.options.params.period = 28;
                    s.update(s.options);
                  }
                });
              }
            }
          },
          buttons: {
            contextButton: {
              menuItems: ["avg7Day", "avg28Day"]
            }
          }
        }
      },
      betStatsOptions: {
        xAxis: {
          type: "datetime"
        },

        yAxis: {
          title: "Value"
        },

        time: {
          useUTC: false
        },

        title: {
          text: "Bet History"
        },

        exporting: {
          enabled: false
        },

        rangeSelector: {
          enabled: false,
          inputEnabled: false,
          buttons: [
            {
              type: "day",
              count: 1,
              text: "1d"
            },
            {
              type: "week",
              count: 1,
              text: "7d"
            },
            {
              type: "month",
              count: 1,
              text: "1m"
            },
            {
              type: "all",
              text: "All"
            }
          ]
        },

        legend: {
          enabled: true,
          layout: "horizontal",
          align: "center",
          verticalAlign: "bottom"
        },

        tooltip: {
          valueDecimals: 2,
          valuePrefix: "$ ",
          split: false,
          shared: true
        },

        series: [
          {
            name: "Profit",
            data: [],
            boostThreshold: 1
          },
          {
            name: "EV",
            data: [],
            boostThreshold: 1
          }
        ]
      }
    };
  },
  created() {
    this.applyFilters = _.debounce(this.applyFilters, 500);
  },
  watch: {
    filters: {
      handler() {
        this.applyFilters();
      },
      deep: true
    }
  },
  computed: {
    allBookiesSelected() {
      return Object.values(this.filters.bookies).every(b => b);
    }
  },
  methods: {
    toggleBookies(select) {
      for (const b of Object.keys(this.filters.bookies)) {
        this.filters.bookies[b] = select;
      }
    },
    applyFilters() {
      console.log("applying filters");

      const filtered = [];

      const dates = this.filters.dates.range;
      for (const bet of this.data.bets) {
        if (!this.filters.bookies[bet.bo]) continue;
        if (!this.filters.race_types[bet.rt]) continue;
        if (!this.filters.markets[bet.mt]) continue;
        if (!this.filters.states[bet.vs]) continue;
        const local = moment(bet.d).format("YYYY-MM-DD");

        if (!this.filters.dates.all) {
          if (this.filters.dates.range.length === 0) continue;

          const dateStart = dates[0];
          const dateEnd =
            dates.length === 1
              ? moment(dateStart)
                  .add(1, "day")
                  .format("YYYY-MM-DD")
              : moment(dates[1])
                  .add(1, "day")
                  .format("YYYY-MM-DD");

          if (local < dateStart || local >= dateEnd) continue;
        }

        filtered.push(bet);
      }

      this.updateCharts(filtered);
    },
    updateBetChart(filtered) {
      const bets = [];
      const ev = [];

      let betCount = 0;
      let totalProfit = 0;
      let totalEV = 0;
      let totalStake = 0;

      let prevProfit = 0;
      let prevEV = 0;

      const TARGET_POINTS = 500;
      const MERGE_NUMBER = Math.ceil(filtered.length / TARGET_POINTS);
      let index = 0;

      for (const bet of filtered) {
        betCount++;
        totalProfit += bet.re - bet.sk;
        totalEV += bet.e;
        totalStake += bet.sk;

        if (index % MERGE_NUMBER === 0 || index === filtered.length - 1) {
          const timestamp = moment(bet.d)
            .startOf("minute")
            .valueOf();
          const current = prevProfit + bet.re - bet.sk;
          bets.push([timestamp, current]);
          ev.push([timestamp, prevEV + bet.e]);
        }

        prevProfit = prevProfit + bet.re - bet.sk;
        prevEV += bet.e;

        if (Number.isNaN(prevEV)) {
          debugger;
        }

        index++;
      }

      this.summary = {
        profit: totalProfit,
        ev: totalEV,
        bets: betCount,
        margin: totalStake ? (100 * totalEV) / totalStake : 0
      };

      const betStats = this.$refs.betStats.chart;
      betStats.xAxis[0].setExtremes();
      betStats.series[0].setData(bets);
      betStats.series[1].setData(ev);
    },
    updateDayChart(filtered) {
      const days = {};
      const minDate = moment("2020-10-07").toISOString();

      for (const bet of filtered) {
        if (bet.d < minDate) continue;

        const timestamp = moment(bet.d)
          .startOf("day")
          .valueOf();

        if (!days[timestamp]) {
          days[timestamp] = {
            profit: 0,
            ev: 0,
            numBets: 0
          };
        }

        days[timestamp].profit += bet.re - bet.sk;
        days[timestamp].ev += bet.e;
        days[timestamp].numBets++;
      }

      const dayStats = this.$refs.dayStats.chart;
      dayStats.series[0].setData(
        Object.entries(days).map(e => [parseInt(e[0]), e[1].ev])
      );
      dayStats.series[1].setData(
        Object.entries(days).map(e => [parseInt(e[0]), e[1].profit])
      );
      dayStats.series[2].setData(
        Object.entries(days).map(e => [parseInt(e[0]), e[1].numBets])
      );

      dayStats.series[3].update();
      dayStats.series[4].update();
      dayStats.series[5].update();
    },
    updateCharts(filtered) {
      this.updateBetChart(filtered);
      this.updateDayChart(filtered);
    },
    async downloadCSV() {
      await History.csv();
    },
    async refresh(skipCache) {
      this.loading = true;
      try {
        if (skipCache) {
          await History.clearCache();
        }

        const ignoreCache = this.$store.state.user.isDemo;
        const history = await History.getAll(!ignoreCache);
        this.csvURL = history.csv;

        this.data.bets = [];
        for (const bet of history.bets) {
          if (bet.st !== "FINAL") continue;

          if (bet.bo === "sportsbetting") {
            bet.bo = "boombet";
          }

          this.data.bets.push(bet);
          Vue.set(this.filters.bookies, bet.bo, true);
        }

        this.applyFilters();
      } catch (err) {
        this.showError(`Unable to read profit history - ${err}`);
      } finally {
        this.loading = false;
      }
    },
    marketDescription(market) {
      if (market === "W") return "Win";
      if (market === "P") return "Place";
      if (market === "T") return "Place (2)";
      if (market === "F") return "Fav v Field";
      return market;
    },
    raceTypeDescription(type) {
      if (type === "T") return "Horses";
      if (type === "H") return "Harness";
      if (type === "G") return "Greyhounds";
      return type;
    },
    formatCurrency(amt) {
      return currency(amt, 2, true);
    }
  },
  mounted() {
    this.refresh();
  }
};
</script>

<style lang="scss">
#stats {
  .load-parent {
    min-height: 300px;
    width: 100%;

    .loader {
      margin: auto;
    }
  }

  .highcharts-credits {
    display: none;
  }

  .v-window__next {
    bottom: 0px;
    top: revert;
  }

  .v-window__prev {
    bottom: 0px;
    top: revert;
  }
}

#stats-filter {
  .v-card {
    width: 300px;
    display: inline-block;
    vertical-align: top;
  }

  #s-bookies {
    width: 340px;
  }

  #s-types {
    width: 180px;
  }

  #s-markets {
    width: 160px;
  }

  #s-states {
    width: 250px;
  }

  #s-dates {
    width: 300px;
  }

  #s-summary {
    width: 270px;
  }
}

.filter-bookie {
  text-transform: capitalize;
}

.filter-type {
  text-transform: capitalize;
}

#s-summary {
  .v-card__text {
    margin-top: 16px;
  }

  .s-item {
    margin-bottom: 16px;

    span {
      display: inline-block;
      font-size: 1.2em;
    }

    .s-name {
      width: 110px;
    }

    .s-value {
      width: 120px;
      text-align: right;
    }
  }
}

.b-csv {
  font-size: 0.55em;
  margin-left: auto;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;

  position: relative;
  top: 2px;
  right: 30px;
}

.b-select {
  font-size: 0.55em;
  margin-left: auto;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;

  position: relative;
  top: 2px;
  right: 60px;
}
</style>
